import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { assertNever } from "@app/types/shared/utils/assert_never";
import { z } from "zod";

export const FILE_GENERATION_TOOL_NAME = "file_generation" as const;

export const OUTPUT_FORMATS = [
  "csv",
  "docx",
  "gif",
  "html",
  "jpg",
  "md",
  "pdf",
  "png",
  "pptx",
  "txt",
  "webp",
  "xls",
  "xlsx",
  "xml",
] as const;

export type OutputFormatType = (typeof OUTPUT_FORMATS)[number];

export const BINARY_FORMATS: OutputFormatType[] = [
  "docx",
  "pdf",
  "pptx",
  "xls",
  "xlsx",
  "gif",
  "jpg",
  "png",
  "webp",
];

export const FILE_GENERATION_TOOLS_METADATA = [
  {
    name: "get_supported_source_formats_for_output_format",
    description:
      "List which input source formats can be converted into a given target output format.",
    schema: {
      output_format: z.enum(OUTPUT_FORMATS).describe("The format to check."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Listing supported formats",
      done: "List supported formats",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "convert_file_format",
    description:
      "Convert an existing conversation file into another format, for example turn a document into a PDF.",
    schema: {
      file_name: z
        .string()
        .describe(
          "The name of the file to generate. Must be a valid filename without the format extension."
        ),
      file_id_or_url: z
        .string()
        .describe(
          "The ID or URL of the file to convert. You can either provide the ID of a file in the conversation (note: if the file ID is already in the desired format, no conversion is needed) or the URL to a file."
        ),
      source_format: z
        .string()
        .describe(
          "The format of the source file. Use the `get_source_format_to_convert_to` tool to get the list of formats you can use."
        ),
      output_format: z
        .enum(OUTPUT_FORMATS)
        .describe("The format of the output file."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Converting file",
      done: "Convert file",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "generate_file",
    description:
      "Generate a new file by writing provided text or content out as a document.",
    schema: {
      file_name: z
        .string()
        .describe(
          "The name of the file to generate. Must be a valid filename with the format extension."
        ),
      file_content: z
        .string()
        .max(64000)
        .describe(
          "The content of the file to generate. You can either provide the id of a file in the conversation (note: if the file ID is already in the desired format, no conversion is needed), the url to a file or the content directly."
        ),
      source_format: z
        .enum(["text", "markdown", "html"])
        .optional()
        .default("text")
        .describe(
          "The format of the input content. Use 'markdown' for markdown-formatted text, 'html' for HTML content, or 'text' for plain text (default)."
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Generating file",
      done: "Generate file",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
] as const;

/**
 * @cc [owner:flvndvd,label:product;mcp] pdf-output-follows-computer-availability
 * When Computer is available, the file generation tool schemas MUST exclude PDF
 * output, including PDF filenames, while preserving other formats. Without
 * Computer, the original PDF generation and conversion schemas MUST remain available.
 */
export function getFileGenerationToolsMetadata({
  hasComputer,
}: {
  hasComputer: boolean;
}) {
  return FILE_GENERATION_TOOLS_METADATA.map((tool) => {
    if (!hasComputer) {
      return tool;
    }

    switch (tool.name) {
      case "get_supported_source_formats_for_output_format":
        return {
          ...tool,
          schema: {
            ...tool.schema,
            output_format: tool.schema.output_format.exclude(["pdf"]),
          },
        };
      case "convert_file_format":
        return {
          ...tool,
          description:
            "Convert an existing conversation file into another supported format. Use the PDFs skill for PDF output.",
          schema: {
            ...tool.schema,
            output_format: tool.schema.output_format.exclude(["pdf"]),
          },
        };
      case "generate_file":
        return {
          ...tool,
          schema: {
            ...tool.schema,
            // The handler's path.extname ignores trailing slashes.
            file_name: tool.schema.file_name
              .refine((name) => !/\.pdf\/*$/i.test(name), {
                message: "Use the PDFs skill in the Computer to create PDFs.",
              })
              .describe(
                "The name of the file to generate, including its extension. " +
                  `Supported formats: ${OUTPUT_FORMATS.filter((format) => format !== "pdf").join(", ")}. ` +
                  "Use the PDFs skill for PDF output."
              ),
          },
        };
      default:
        return assertNever(tool);
    }
  });
}

export const FILE_GENERATION_SERVER = {
  serverInfo: {
    name: "file_generation" as const,
    version: "1.0.0",
    description: "Generate and convert documents.",
    authorization: null,
    icon: "ActionDocumentTextIcon" as const,
    documentationUrl: null,
  },
  tools: FILE_GENERATION_TOOLS_METADATA,
} as const satisfies ServerMetadata;
